/**
 * Operator password reset — the "forgot my password" escape hatch.
 *
 * An admin types a NEW password for the target user (no old-password
 * proof; self-service change stays at /auth/change-password with the
 * old one). Mirrors the reset-2fa escape hatch's placement: an icon
 * action on the Users table row.
 */
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { usersApi } from "@/lib/api-users";

export function SetPasswordDialog({
  target,
  onClose,
}: {
  /** User to reset, or null when the dialog is closed. */
  target: { id: string; username: string } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  // Reset the form whenever a new target opens the dialog.
  React.useEffect(() => {
    setPassword("");
    setConfirm("");
    setError(null);
  }, [target?.id]);

  const save = useMutation({
    mutationFn: () => usersApi.setPassword(target!.id, password),
    onSuccess: () => onClose(),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t("users.setPasswordError")),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("users.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("users.passwordMismatch"));
      return;
    }
    save.mutate();
  }

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {t("users.setPasswordTitle", { username: target?.username ?? "" })}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("users.setPasswordHelp")}</p>
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{t("users.newPassword")}</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">{t("users.confirmPassword")}</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={save.isPending || !password || !confirm}>
              {t("users.setPasswordConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
